import {
  Badge,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import Iconify from "../Iconify";
import {
  _Get_Notifications_Data,
  _Mark_All_Notifications_As_Read,
  _Mark_Notification_As_Read,
} from "../../DAL/Listing/Notification";
import moment from "moment";
import { useUserData } from "../../Hooks/App_Context";
import { useNavigate } from "react-router-dom";
import {
  notificationPaperStyle,
  NotificationsIconProvider,
  styleForLoadMoreButton,
} from "../../utils/constant";

export const Notifications = ({ ProfileInfo }) => {
  const navigate = useNavigate();
  const [initLoad, setInitLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [HasMore, setHasMore] = useState(true);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const {
    newNotification = [],
    setNewNotification,
    userData = {},
    setisNewMessage,
    setNotificationMessage,
  } = useUserData();

  // Handle navigation based on the notification type && handle Reload feature if standing on the same page
  const handleNavigation = (status, notification) => {
    if (notification?.notification_type === "ad_account") {
      navigate(`/agency-accounts`, { state: { refresh: Date.now() } });
    } else if (notification?.notification_type === "clear_funds") {
      navigate(`/clear-account-funds`, { state: { refresh: Date.now() } });
    } else if (notification?.notification_type === "topup_request") {
      navigate(`/top-up-funds`, { state: { refresh: Date.now() } });
    } else if (notification?.notification_type === "appeal_account") {
      navigate(`/appeal-disabled-account`, { state: { refresh: Date.now() } });
    } else if (
      notification?.for === "clear_wallet" &&
      notification?.notification_type === "transaction"
    ) {
      navigate(`/clear-wallet-balance/details/${notification?.module_id}`);
    } else if (notification?.notification_type === "transaction") {
      navigate(`/deposit-request/deposit-detail/${notification?.module_id}`);
    } else if (notification?.notification_type === "affiliate_payout") {
      navigate(`/affiliate-payout-request`, { state: { refresh: Date.now() } });
    } else if (notification?.notification_type === "subscription") {
      navigate(`/cancel-subscription-requests`, {
        state: { refresh: Date.now() },
      });
    } else if (notification?.notification_type === "support_ticket") {
      navigate(`/support-ticket/${notification?.module_id}`);
    } else if (notification?.notification_type === "chat_message") {
      setisNewMessage(true);
      setNotificationMessage(notification);
    }
    // Close the notification anchor after handling
    setNotificationAnchor(null);
  };

  // Handle menu click
  const handleNotificationClick = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  // Mark a notification as read and update the UI state
  const handleNotificationRead = async (notificationId) => {
    const response = await _Mark_Notification_As_Read(notificationId);
    if (response.code === 200) {
      // Update the notification as read in local state
      setNewNotification((prevNotifications) =>
        prevNotifications?.map((notif) =>
          notif._id === notificationId ? { ...notif, is_read: true } : notif
        )
      );

      // Decrease the unread count safely
      setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
    } else {
      console.error("Error marking notification as read");
    }
  };

  // Mark all notifications as read and refresh the notification list
  const markAllAsRead = async () => {
    await _Mark_All_Notifications_As_Read();
    GetNotificationsData(0, 50); // Reload notifications
  };

  // Load the next page of notifications and update the state
  const loadMoreNotifications = () => {
    setLoading(true);
    const nextPage = page + 1;
    GetNotificationsData(nextPage, 50);
    setPage(nextPage);
  };

  // Handle the close event of the notification menu
  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  // Fetch notifications and process read/unread status
  const GetNotificationsData = async (page, rows) => {
    setInitLoad(true);
    const response = await _Get_Notifications_Data(page, rows);
    if (response.code === 200) {
      const loggedInUserId = userData?.user_id?._id;

      // Attach read status and formatted time to each notification
      const notificationsWithIcons = response.notifications?.map(
        (notification) => {
          const isUnread = notification.unread_users?.includes(loggedInUserId);
          return {
            ...notification,
            is_read: !isUnread,
            time: moment(notification.notification_date_time).fromNow(),
          };
        }
      );

      // Merge notifications correctly
      const updatedNotifications =
        page === 0
          ? notificationsWithIcons
          : [...newNotification, ...notificationsWithIcons];

      setNewNotification(updatedNotifications);

      // Recalculate unread count from scratch
      const newUnreadCount = updatedNotifications?.filter(
        (n) => !n.is_read
      )?.length;
      setUnreadCount(newUnreadCount);

      setHasMore(response?.notifications?.length >= rows);
    } else {
      console.error("Error fetching notifications");
    }
    setLoading(false);
    setInitLoad(false);
  };

  useEffect(() => {
    GetNotificationsData(0, 50);
    // eslint-disable-next-line
  }, [userData]);

  useEffect(() => {
    const newUnreadCount = newNotification?.filter((n) => !n.is_read)?.length;
    setUnreadCount(newUnreadCount);
    // eslint-disable-next-line
  }, [newNotification]);

  return (
    <>
      <Badge
        badgeContent={unreadCount}
        color="error"
        max={50}
        sx={{
          "& .MuiBadge-badge": {
            lineHeight: "13px",
            fontSize: "10px",
          },
        }}
      >
        <div
          className="position-relative d-inline-block"
          onClick={(event) => {
            handleNotificationClick(event);
            GetNotificationsData(0, 50);
          }}
        >
          <Box className="notification-container pointer">
            <Iconify
              className="notification-bell fs-4"
              icon="line-md:bell-loop"
            />
          </Box>
        </div>
      </Badge>

      {/* Notifications Dropdown */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        transitionDuration={600}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={notificationPaperStyle}
      >
        <div className="d-flex flex-wrap align-items-center mb-2 border-1 border-bottom border-dark">
          <div className="col-12 col-md-8">
            <Typography
              style={{
                fontWeight: 600,
                fontSize: "0.89rem",
              }}
              sx={{ px: 2, py: 0.5 }}
            >
              Notifications
            </Typography>
            <Typography
              style={{
                fontWeight: 400,
                fontSize: "0.89rem",
              }}
              sx={{ px: 2, pb: 1, color: "gray" }}
            >
              {unreadCount} unread notifications
            </Typography>
          </div>
          <div className="d-flex align-items-start justify-content-center col-12 col-md-4">
            <IconButton onClick={markAllAsRead} className="">
              <Iconify
                icon="mdi:tick-all"
                sx={{
                  fontSize: "24px",
                  color: "primary.main",
                }}
              />
            </IconButton>
          </div>
        </div>
        <div className="list-item-main">
          <>
            {newNotification?.length > 0 ? (
              <>
                {newNotification?.map((notif, index) => {
                  return (
                    <>
                      <MenuItem
                        className="w-100"
                        onClick={(e) => {
                          handleNavigation(notif.is_read, notif);
                          !notif.is_read && handleNotificationRead(notif._id);
                        }}
                        key={index}
                        sx={{
                          width: "360px",
                          backgroundColor: notif.is_read
                            ? "inherit"
                            : "#6d00fc1A",
                          color: notif.is_read ? "#ACACAC" : "inherit",
                          padding: "8px 16px",
                          cursor: "pointer",
                          borderRadius: 1,
                          transition: "background-color 0.3s ease",
                          margin: "4px 0px",
                          "&:hover": {
                            backgroundColor: "#8080801f",
                          },
                        }}
                      >
                        <div className="me-3 notification-icon-background">
                          <Iconify
                            className={"notification-icon"}
                            icon={NotificationsIconProvider(notif)}
                          />
                        </div>
                        <div>
                          <Typography
                            variant="body2"
                            style={{
                              textWrap: "wrap",
                            }}
                          >
                            {notif.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {notif.time}
                          </Typography>
                        </div>
                      </MenuItem>
                      {notif.is_read && (
                        <Divider
                          sx={{
                            my: 0.5,
                            borderColor: "white", // Set divider color to white
                          }}
                        />
                      )}
                    </>
                  );
                })}
              </>
            ) : (
              <Box
                sx={{
                  width: "360px",
                  height: "350px",
                  textAlign: "center",
                  py: 3,
                  color: "#999",
                }}
              >
                <Iconify
                  icon="mdi:bell-off-outline"
                  style={{ fontSize: 36, marginBottom: 8 }}
                />
                <Typography variant="body2">
                  You have no notifications yet.
                </Typography>
              </Box>
            )}
          </>
          {HasMore && newNotification?.length > 0 && (
            <div className="col-12 d-flex my-2 d-flex justify-content-center">
              <Button
                variant="text"
                size="small"
                color="primary"
                fullWidth
                onClick={loadMoreNotifications}
                disabled={loading}
                sx={styleForLoadMoreButton}
              >
                {loading ? (
                  <CircularProgress size={25} color="inherit" />
                ) : (
                  "View More"
                )}
              </Button>
            </div>
          )}
        </div>
      </Menu>
    </>
  );
};
